import React, { useState } from 'react';
import { Spinner } from '@/components/Spinner';
import { ErrorMessage } from '@/components/ErrorMessage';
import { PasswordToggle } from './PasswordToggle';
import { AlertCircle } from './icons/AlertCircle';
import { SuccessCircle } from './icons/SuccessCircle';

export interface InputProps {
  id: string;
  name: string;
  label: string;
  placeholder?: string;
  type: string;
  value: string | undefined;
  onChange: (value: string) => void;
  required?: boolean;
  errorMessage?: string | React.ReactNode;
  isLoading?: boolean;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  className?: string;
  showPasswordToggle?: boolean;
  hasError?: boolean;
  readonly?: boolean;
}

export const Input = (props: InputProps) => {
  const [showPassword, setShowPassword] = useState(false);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    props.onChange && props.onChange(e.target.value);
  }

  const errorMessageComponent =
    typeof props.errorMessage === 'string' ? <ErrorMessage error={props.errorMessage} /> : props.errorMessage;

  const isSuccess = props.value && !props.hasError && !props.isLoading && !props.readonly;

  // Determine input type based on toggle
  const inputType = props.showPasswordToggle ? (showPassword ? 'text' : 'password') : props.type;

  const baseClassName =
    'tw:h-12 tw:px-3 tw:w-full tw:text-base tw:font-extralight tw:text-black tw:rounded-[0.625rem]';
  const defaultClassName = 'tw:bg-white tw:border tw:border-grey-30';
  const focusedClassName =
    'tw:focus:border-2 tw:focus:border-grey-30 tw:focus:shadow-[0_0_2px_2px_#D9D9D9] tw:outline-hidden';
  const successClassName = 'tw:bg-[#F8FDF9] tw:border-2 tw:border-[#00731D]';
  const errorClassName = 'tw:bg-[#FFF8F5] tw:border-2 tw:border-[#C21515] tw:pr-12';
  const readOnlyClassName = 'tw:bg-[#D9D9D9]';

  let inputClassName = `${baseClassName} ${focusedClassName}`;

  // Add border styles based on state
  if (props.hasError) {
    inputClassName += ` ${errorClassName}`;
  } else if (isSuccess) {
    inputClassName += ` ${successClassName}`;
  } else {
    inputClassName += ` ${defaultClassName}`;
  }

  if (props.readonly) {
    inputClassName += ` ${readOnlyClassName}`;
  }

  return (
    <div className={`tw:w-full tw:relative ${props.className}`}>
      <label htmlFor={props.id}>
        <div className="tw:flex tw:justify-between tw:items-center tw:mb-1">
          <span>{props.label}</span>
          {props.required && <span className="tw:text-gray-400">Required</span>}
        </div>
        <div className="tw:flex tw:justify-between tw:items-center tw:relative">
          {props.isLoading && (
            <span className="tw:absolute tw:inset-y-0 tw:right-3 tw:flex tw:items-center tw:pr-2">
              <Spinner size="md" />
            </span>
          )}

          <input
            key="props"
            id={props.id}
            name={props.id}
            className={`${inputClassName} ${isSuccess ? 'success-input' : ''} ${
              props.hasError ? 'error-input' : ''
            } ${props.className ?? ''}`}
            type={inputType}
            placeholder={props.placeholder}
            onChange={handleChange}
            onBlur={props.onBlur}
            value={props.value || ''}
            required={props.required}
            data-testid={props.id}
          />

          {isSuccess && (
            <SuccessCircle
              className={`tw:absolute tw:w-6 ${props.showPasswordToggle ? 'tw:right-12' : 'tw:right-3'} tw:top-3`}
            />
          )}
          {props.hasError && (
            <AlertCircle
              className={`tw:absolute tw:w-6 ${props.showPasswordToggle ? 'tw:right-12' : 'tw:right-3'} tw:top-3`}
            />
          )}

          {props.showPasswordToggle && (
            <PasswordToggle
              showPassword={showPassword}
              onToggle={() => setShowPassword(!showPassword)}
              id={props.id}
            />
          )}
        </div>
        {props.errorMessage && errorMessageComponent}
      </label>
    </div>
  );
};
